import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../../../service/auth-service";
import Loading from "../../dialog/loading/loading";
import "./delete-account-style.css";

//刪除帳號頁（公開；用於 Google 審核規範「用戶可自行刪除資料」）
//  輸入 Email 或 User ID 其中一個 → 呼叫 A-3-1 刪除對應帳號 + Plate + 用戶頭像
//  獨立頁、不繼承 MainLayout；嚴肅風格、強制勾選同意才可送出
const DeleteAccountPage = () => {
  const navigate = useNavigate();

  //輸入模式：email / userID，影響 input placeholder、驗證規則
  const [mode, setMode] = useState("email");
  const [value, setValue] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  //結果狀態：null 未送 / "success" 刪除成功 / "notfound" 找不到 / "error" 出錯
  const [result, setResult] = useState(null);
  const [resultMsg, setResultMsg] = useState("");

  //簡易驗證：email 格式；userID 任何非空字串
  const trimmed = value.trim();
  const isValidInput =
    mode === "email"
      ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
      : trimmed.length > 0;

  const canSubmit = isValidInput && acknowledged && !submitting;

  const handleModeSwitch = (next) => {
    if (submitting) return;
    setMode(next);
    setValue("");
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setResult(null);
    setResultMsg("");
    try {
      const payload =
        mode === "email" ? { userEmail: trimmed } : { userID: trimmed };
      const res = await AuthService.deleteAccountPublic(payload);
      const code = res?.data?.data?.resultCode;
      if (code === "1") {
        setResult("success");
        setResultMsg("您的帳號與相關資料已永久刪除。");
      } else {
        setResult("notfound");
        setResultMsg("找不到該用戶，或資料早已刪除。");
      }
    } catch (e) {
      setResult("error");
      setResultMsg(
        e?.response?.data?.message || "刪除失敗，請稍後再試或聯繫客服。",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setValue("");
    setAcknowledged(false);
    setResult(null);
    setResultMsg("");
  };

  return (
    <div className="delete-account-page">
      <div className="delete-account-card">
        <header className="delete-account-header">
          <h1>刪除帳號</h1>
          <p className="brand">元辰雲閣 YuanChen</p>
        </header>

        {/* 刪除成功 / 失敗結果區（蓋掉表單） */}
        {result ? (
          <div className={`delete-account-result delete-account-result--${result}`}>
            <div className="icon">
              {result === "success" ? "✓" : result === "notfound" ? "ⓘ" : "⚠"}
            </div>
            <h2>
              {result === "success"
                ? "帳號刪除完成"
                : result === "notfound"
                  ? "找不到該帳號"
                  : "操作失敗"}
            </h2>
            <p>{resultMsg}</p>
            <div className="actions">
              {result === "success" ? (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => navigate("/")}
                >
                  返回首頁
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => navigate("/")}
                  >
                    返回首頁
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleReset}
                  >
                    重新輸入
                  </button>
                </>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* 警告訊息區 */}
            <div className="delete-account-warning">
              <h2>⚠️ 刪除後將永久移除：</h2>
              <ul>
                <li>您的個人命盤與基本資料</li>
                <li>所有購買紀錄與已解鎖報告</li>
                <li>諮詢對話紀錄與剩餘 A-Point</li>
                <li>所有合盤 / 流年之書 / 奇門遁甲資料</li>
              </ul>
              <p className="strong">此操作無法復原，請謹慎確認。</p>
            </div>

            {/* 模式切換 tab */}
            <div className="delete-account-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={mode === "email"}
                className={mode === "email" ? "active" : ""}
                onClick={() => handleModeSwitch("email")}
              >
                以 Email 刪除
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === "userID"}
                className={mode === "userID" ? "active" : ""}
                onClick={() => handleModeSwitch("userID")}
              >
                以 User ID 刪除
              </button>
            </div>

            {/* 輸入欄位 */}
            <div className="delete-account-field">
              <label htmlFor="delete-input">
                {mode === "email" ? "註冊時使用的 Email" : "帳號 User ID"}
              </label>
              <input
                id="delete-input"
                type={mode === "email" ? "email" : "text"}
                placeholder={
                  mode === "email" ? "yourname@example.com" : "US12345678"
                }
                value={value}
                onChange={(e) => setValue(e.target.value)}
                disabled={submitting}
                autoComplete="off"
                spellCheck={false}
              />
              {value && !isValidInput && (
                <p className="field-hint error">
                  {mode === "email"
                    ? "請輸入有效的 Email 格式"
                    : "User ID 不可為空"}
                </p>
              )}
            </div>

            {/* 同意勾選 */}
            <label className="delete-account-ack">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                disabled={submitting}
              />
              <span>我已瞭解此操作無法復原，所有資料將被永久刪除。</span>
            </label>

            {/* 操作按鈕 */}
            <div className="delete-account-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate("/")}
                disabled={submitting}
              >
                取消
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                {submitting ? "刪除中…" : "確認刪除帳號"}
              </button>
            </div>

            {/* 說明與聯繫 */}
            <footer className="delete-account-footer">
              <p>如有疑問，請來信 yuanchencloud@gmail.com。</p>
            </footer>
          </>
        )}
      </div>

      {submitting && <Loading />}
    </div>
  );
};

export default DeleteAccountPage;
